import RecoverableImage from '../images/RecoverableImage';
import { profileWords } from '../profile/profileConnectionModel';
import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FiChevronRight } from 'react-icons/fi';
import useMyRecords from '../../pages/mypage/components/useMyRecords';
import { recordPath } from '../../pages/mypage/components/myRecordsModel';
import MeasuredContentTitle from '../../components/content/MeasuredContentTitle';
import HydratedPlaylistArtwork from '../../components/content/HydratedPlaylistArtwork';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { useUserStore } from '../../stores/userStore';
import { supportApi } from '../sponsorship/api';
import { BadgeIcon, SupportMark } from './BadgeIcon';
import useBadgeStyle from './useBadgeStyle';
import useBadges from './useBadges';
import { recentActivities, earnedBadges, words } from './model';
import './badges.css';
import { BadgeSkeleton } from './States';
export function SectionLink({ title, to }) {
  return (
    <h2 className="pp-badge-section-title">
      <Link to={to}>
        {title}
        <FiChevronRight size={20} />
      </Link>
    </h2>
  );
}
export default function MyActivity({ profile }) {
  const records = useMyRecords(profile);
  return <ActivityRecords records={records} />;
}
export function ActivityRecords({ records, title, to = '/mypage/ratings' }) {
  const { locale } = useWebLocale(),
    t = words(locale),
    style = useBadgeStyle(),
    { enabled, data, queries } = records;
  if (!enabled) return null;
  const rows = recentActivities(data.albums, data.lists),
    loading = queries.albums.isPending || queries.lists.isPending,
    error = queries.albums.isError || queries.lists.isError;
  return (
    <section className="pp-badges pp-my-recent" style={style}>
      <SectionLink title={title || t('my.recentActivity')} to={to} />
      {error && (
        <p role="alert">
          {locale === 'en' ? 'Could not load recent activity.' : '최근 활동을 불러오지 못했어요.'}
          <button
            onClick={() => {
              void queries.albums.refetch();
              void queries.lists.refetch();
            }}
          >
            {t('common.retry')}
          </button>
        </p>
      )}
      {loading && !rows.length ? (
        <BadgeSkeleton rows={2} />
      ) : !rows.length && !error ? (
        <div className="pp-badge-empty">
          <strong>{t('activity.empty')}</strong>
          <p>{t('activity.emptyBody')}</p>
        </div>
      ) : (
        rows.map(({ key, kind, item }) => {
          const pl = kind === 'playlist',
            path = recordPath(item, pl ? 'lists' : kind === 'review' ? 'reviews' : 'ratings');
          return (
            <Link className="pp-recent-row" key={key} to={path || to}>
              <div className="pp-recent-art">
                {pl ? (
                  <HydratedPlaylistArtwork playlist={item} />
                ) : item.album.imageUrl ? (
                  <RecoverableImage src={item.album.imageUrl} alt="" />
                ) : null}
              </div>
              <div className="pp-recent-copy">
                <small className="pp-badge-accent">{t('activity.' + kind)}</small>
                <MeasuredContentTitle
                  title={pl ? item.title : item.album.title}
                  explicit={!pl && item.album.contentRating === 'EXPLICIT'}
                />
                {pl ? (
                  <small>
                    {t('activity.collectionCount', {
                      count: (item.type === 'ALBUM' ? item.albumCount : item.trackCount) ?? 0,
                      type: t(item.type === 'ALBUM' ? 'activity.albumType' : 'activity.trackType'),
                    })}
                  </small>
                ) : (
                  <span className="pp-recent-meta">
                    <span>{item.artist?.name ?? t('common.artist')}</span>
                    <span>
                      {' '}
                      ·{' '}
                      <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
                        <path
                          fill="#F16438"
                          transform="translate(0 .955)"
                          d="M12 2L14.47 8.6L21.51 8.91L15.99 13.3L17.88 20.09L12 16.2L6.12 20.09L8.01 13.3L2.49 8.91L9.53 8.6L12 2Z"
                        />
                      </svg>{' '}
                      {Number(item.review.rating ?? 0).toFixed(1)}
                    </span>
                  </span>
                )}
              </div>
              <FiChevronRight size={17} />
            </Link>
          );
        })
      )}
    </section>
  );
}
export function BadgeEntry({ profile, publicProfile = false }) {
  const user = useUserStore((s) => s.user),
    owned =
      !!user?.id && String(user.id) === String(profile.id) && user.nickname === profile.nickname;
  const q = useBadges(publicProfile ? profile.nickname : undefined, publicProfile || owned),
    { locale } = useWebLocale(),
    t = words(locale),
    style = useBadgeStyle(),
    earned = earnedBadges(q.data).slice(0, 3);
  if (!owned && !publicProfile) return null;
  const to = publicProfile
    ? `/users/${encodeURIComponent(profile.nickname)}/badges`
    : '/mypage/badges';
  const publicT = profileWords(locale);
  const marks = earned.length
    ? earned
    : [
        { key: 'recording', category: 'RECORDING', iconKey: 'disc', tier: 1 },
        { key: 'exploration', category: 'EXPLORATION', iconKey: 'compass', tier: 2 },
        { key: 'community', category: 'COMMUNITY', iconKey: 'community', tier: 3 },
      ];
  return (
    <section className="pp-badges pp-my-badges" style={style}>
      <SectionLink title={t(publicProfile ? 'badges.title' : 'my.activityBadges')} to={to} />
      <Link className="pp-badge-entry" to={to}>
        <span className="pp-badge-entry-marks">
          {marks.map((b) => (
            <BadgeIcon key={b.key} badge={b} size={34} muted={!earned.length} />
          ))}
        </span>
        <span>
          <strong>
            {publicProfile
              ? publicT('profile.badgeCollection', { nickname: profile.nickname })
              : t('my.badgeCollectionPrompt')}
          </strong>
          <small>
            {q.isError
              ? t('badges.loadError')
              : q.data
                ? publicProfile
                  ? publicT('profile.earnedBadgesCount', { count: q.data.earnedCount })
                  : t('my.badgesEarned', { count: q.data.earnedCount })
                : publicProfile
                  ? publicT('profile.badgesLoading')
                  : t('my.badgesLoading')}
          </small>
        </span>
        <FiChevronRight size={20} />
      </Link>
    </section>
  );
}
export function MySupportEntry() {
  const user = useUserStore((s) => s.user),
    { locale } = useWebLocale(),
    style = useBadgeStyle(),
    q = useQuery({
      queryKey: ['support-promotion'],
      queryFn: ({ signal }) => supportApi.promotion(signal),
      retry: false,
      staleTime: 60000,
      refetchInterval: 60000,
    }),
    status = useQuery({
      queryKey: ['sponsorships', 'me', user?.id],
      queryFn: ({ signal }) => supportApi.mine(signal),
      enabled: !!user?.id,
      staleTime: 60000,
      retry: false,
    });
  if (!user?.id || !q.isSuccess || q.data?.myEnabled === false) return null;
  const supporter = status.data?.isSupporter === true,
    index = Math.max(0, Math.min(2, (q.data.myVariant ?? 1) - 1));
  const variants =
    locale === 'en'
      ? [
          ['Be part of Postpop', 'Explore support and benefits'],
          ['Keep our music stories going', 'Help shape what comes next for Postpop'],
          ['More time to talk about the music we love', 'Discover Postpop support and benefits'],
        ]
      : [
          ['포스트팝과 함께해주세요', '포스트팝 후원 혜택 알아보기'],
          ['우리의 음악 이야기가 계속되도록', '후원으로 포스트팝의 다음을 함께 만들어주세요'],
          ['좋아하는 음악을 오래 이야기할 수 있도록', '포스트팝 후원과 혜택을 소개합니다'],
        ];
  const text = supporter
    ? locale === 'en'
      ? ['Thank you for being with us', 'Explore your benefits']
      : ['함께해주셔서 감사합니다', '혜택 알아보기']
    : variants[index];
  return (
    <Link
      className="pp-badges pp-my-support"
      style={style}
      to={supporter ? '/sponsorship/benefits' : '/sponsorship'}
    >
      <SupportMark size={36} level={1} />
      <span>
        <strong>{text[0]}</strong>
        <small>{text[1]}</small>
      </span>
      <FiChevronRight size={18} />
    </Link>
  );
}
